import * as nacl from 'tweetnacl';
import { DialogController } from '../controllers/dialog';
import { u8aToHex } from '../helpers';
import { scorePassword } from '../helpers/password_strength';
import { InMemorySession } from '../helpers/in_memory_session';
class WelcomeController {
    static BROADCAST_DELAY = 100;
    static REDIRECT_DELAY = 500;
    $scope;
    $window;
    $state;
    $mdDialog;
    $translate;
    webClientService;
    trustedKeyStore;
    pushService;
    stateService;
    settingsService;
    timeoutService;
    config;
    log;
    name = 'welcome';
    mode;
    qrCode;
    password = '';
    passwordStrength = { score: 0, strength: "bad" };
    formLocked = false;
    pleaseUpdateAppMsg = null;
    browser;
    browserWarningShown = false;
    inMemorySession = new InMemorySession();
    static $inject = [
        '$scope', '$state', '$window', '$mdDialog', '$translate',
        'LogService', 'WebClientService', 'TrustedKeyStore', 'StateService', 'PushService', 'BrowserService',
        'VersionService', 'SettingsService', 'TimeoutService', 'ControllerService',
        'BROWSER_MIN_VERSIONS', 'CONFIG',
    ];
    constructor($scope, $state, $window, $mdDialog, $translate, logService, webClientService, trustedKeyStore, stateService, pushService, browserService, versionService, settingsService, timeoutService, controllerService, minVersions, config) {
        controllerService.setControllerName('welcome');
        this.$scope = $scope;
        this.$state = $state;
        this.$window = $window;
        this.$mdDialog = $mdDialog;
        this.$translate = $translate;
        this.webClientService = webClientService;
        this.trustedKeyStore = trustedKeyStore;
        this.stateService = stateService;
        this.pushService = pushService;
        this.settingsService = settingsService;
        this.timeoutService = timeoutService;
        this.config = config;
        if (this.$window.app === undefined) {
            this.$window.app = {};
        }
        this.$window.app.settingsService = settingsService;
        this.$window.app.stateService = stateService;
        this.log = logService.getLogger('Welcome-C');
        this.browser = browserService.getBrowser();
        const version = this.browser.version;
        this.log.info('Detected browser:', this.browser.description());
        if (!this.browser.wasDetermined()) {
            this.log.warn('Could not determine browser version');
            this.showBrowserWarning();
        }
        else if (this.browser.name === "chrome") {
            if (version < minVersions.CHROME) {
                this.log.warn('Chrome is too old (' + version + ' < ' + minVersions.CHROME + ')');
                this.showBrowserWarning();
            }
        }
        else if (this.browser.name === "firefox") {
            if (version < minVersions.FF) {
                this.log.warn('Firefox is too old (' + version + ' < ' + minVersions.FF + ')');
                this.showBrowserWarning();
            }
        }
        else if (this.browser.name === "firefoxIos") {
            if (version < minVersions.FF_IOS) {
                this.log.warn('Firefox on iOS is too old (WebKit ' + version + ' < ' + minVersions.FF_IOS + ')');
                this.showBrowserWarning();
            }
        }
        else if (this.browser.name === "opera") {
            if (version < minVersions.OPERA) {
                this.log.warn('Opera is too old (' + version + ' < ' + minVersions.OPERA + ')');
                this.showBrowserWarning();
            }
        }
        else if (this.browser.name === "safari") {
            if (version < minVersions.SAFARI) {
                this.log.warn('Safari is too old (' + version + ' < ' + minVersions.SAFARI + ')');
                this.showBrowserWarning();
            }
        }
        else if (this.browser.name === "edge") {
            if (version < minVersions.EDGE) {
                this.log.warn('Edge is too old (' + version + ' < ' + minVersions.EDGE + ')');
                this.showBrowserWarning();
            }
        }
        else {
            this.log.warn('Non-supported browser, please use Chrome, Firefox, Edge or Opera');
            this.showBrowserWarning();
        }
        if (this.trustedKeyStore.blocked === true) {
            this.log.error('Cannot access local storage. Is it being blocked by a browser add-on?');
            this.showLocalStorageWarning();
        }
        versionService.initVersion();
        if (this.config.PREV_PROTOCOL_LAST_VERSION !== null) {
            this.pleaseUpdateAppMsg = this.$translate.instant('troubleshooting.PLEASE_UPDATE_APP');
            if (!this.config.SELF_HOSTED) {
                this.pleaseUpdateAppMsg += ' ' + this.$translate.instant('troubleshooting.USE_ARCHIVE_VERSION', {
                    archiveUrl: `https://web.threema.ch/archive/${this.config.PREV_PROTOCOL_LAST_VERSION}/`,
                });
            }
        }
        this.webClientService.clearCache();
        let hasTrustedKey = null;
        try {
            hasTrustedKey = this.trustedKeyStore.hasTrustedKey();
        }
        catch (e) {
            this.log.error('Exception while accessing local storage:', e);
            this.showLocalStorageException(e);
        }
        if (hasTrustedKey
            && this.trustedKeyStore.isAutoSession()
            && this.inMemorySessionPasswordEnabled
            && this.inMemorySessionPassword === undefined) {
            this.log.debug('Found stale auto session password, clearing');
            this.trustedKeyStore.clearTrustedKey();
            hasTrustedKey = false;
        }
        if (hasTrustedKey) {
            this.mode = 'unlock';
            this.unlock();
        }
        else {
            this.mode = 'scan';
            this.scan();
        }
    }
    get showLoadingIndicator() {
        switch (this.stateService.connectionBuildupState) {
            case 'push':
            case 'peer_handshake':
            case 'loading':
            case 'done':
                return true;
            default:
                return false;
        }
    }
    get inMemorySessionPasswordEnabled() {
        return this.config.IN_MEMORY_SESSION_PASSWORD
            && this.inMemorySession.storeAvailable();
    }
    get inMemorySessionPassword() {
        if (!this.inMemorySessionPasswordEnabled) {
            return undefined;
        }
        return this.inMemorySession.getPassword();
    }
    get lastConnectedClientOS() {
        try {
            const lastConnectedClientOS = localStorage.getItem('lastConnectedClientOS');
            if (lastConnectedClientOS === 'ios') {
                return "ios";
            }
            else if (lastConnectedClientOS === 'android') {
                return "android";
            }
            else {
                return undefined;
            }
        }
        catch (error) {
            this.log.error(`Could not read from localStorage: ${error}`);
        }
    }
    get lastConnectedAppFlavor() {
        try {
            const lastConnectedAppFlavor = localStorage.getItem('lastConnectedAppFlavor');
            if (lastConnectedAppFlavor === 'consumer') {
                return 'consumer';
            }
            else if (lastConnectedAppFlavor === 'work') {
                return 'work';
            }
            else {
                return undefined;
            }
        }
        catch (error) {
            this.log.error(`Could not read from localStorage: ${error}`);
        }
    }
    get state() {
        return this.stateService.connectionBuildupState;
    }
    get progress() {
        return this.stateService.progress;
    }
    get slowConnect() {
        return this.stateService.slowConnect;
    }
    get showWebrtcTroubleshooting() {
        return this.webClientService.chosenTask === "webrtc";
    }
    scan(stopArguments) {
        this.log.info('Initialize session by scanning QR code...');
        this.webClientService.stop(stopArguments !== undefined ? stopArguments : {
            reason: "stop",
            send: false,
            close: 'welcome',
            connectionBuildupState: this.stateService.connectionBuildupState,
        });
        this.webClientService.init({
            resume: false,
        });
        this.$scope.$watch(() => this.password, () => {
            const payload = this.webClientService.buildQrCodePayload(this.inMemorySessionPasswordEnabled || this.password.length > 0);
            this.qrCode = this.buildQrCode(payload);
            this.passwordStrength = scorePassword(this.password);
        });
        this.setupBroadcastChannel(this.webClientService.salty.keyStore.publicKeyHex, 0)
            .then((result) => {
            this.$scope.$apply(() => {
                switch (result) {
                    case 'already_open':
                        this.log.warn('Session already connected in another tab or window');
                        break;
                    case 'no_answer':
                        this.start();
                        break;
                }
            });
        })
            .catch((error) => {
            this.$scope.$apply(() => {
                this.log.warn('Unable to set up broadcast channel:', error);
                this.start();
            });
        });
    }
    unlock() {
        this.stateService.reset('new');
        this.log.info('Initialize session by unlocking trusted key...');
        const sessionPassword = this.inMemorySessionPassword;
        if (sessionPassword !== undefined) {
            this.password = sessionPassword;
        }
    }
    unlockConfirm() {
        this.formLocked = true;
        this.trustedKeyStore.retrieveTrustedKey(this.password).then((decrypted) => {
            this.$scope.$apply(() => {
                if (decrypted === null) {
                    this.formLocked = false;
                    return this.showDecryptionFailed();
                }
                const keyStore = new saltyrtcClient.KeyStore(decrypted.ownSecretKey);
                this.setupBroadcastChannel(keyStore.publicKeyHex, WelcomeController.BROADCAST_DELAY)
                    .then((result) => {
                    this.$scope.$apply(() => {
                        switch (result) {
                            case 'already_open':
                                this.log.warn('Session already connected in another tab or window');
                                break;
                            case 'no_answer':
                                this.log.debug('No broadcast received indicating that a session is already open');
                                this.reconnect(keyStore, decrypted);
                                break;
                        }
                    });
                })
                    .catch((error) => {
                    this.$scope.$apply(() => {
                        this.log.warn('Unable to set up broadcast channel:', error);
                        this.reconnect(keyStore, decrypted);
                    });
                });
            });
        }).catch((error) => {
            this.log.warn('Unable to unlock:', error);
        });
    }
    setupBroadcastChannel(publicKeyHex, delayMs) {
        const future = new Future();
        if (!('BroadcastChannel' in this.$window)) {
            future.reject('BroadcastChannel not supported in this browser');
            return future;
        }
        const CHANNEL_NAME = 'session-check';
        const TYPE_PUBLIC_KEY = 'public-key';
        const TYPE_ALREADY_OPEN = 'already-open';
        const channel = new BroadcastChannel(CHANNEL_NAME);
        channel.onmessage = (event) => {
            const message = JSON.parse(event.data);
            switch (message.type) {
                case TYPE_PUBLIC_KEY:
                    if (message.key === publicKeyHex
                        && (this.stateService.connectionBuildupState === 'loading'
                            || this.stateService.connectionBuildupState === 'done')) {
                        this.log.debug('Another tab is trying to connect to our session. Respond with a broadcast.');
                        channel.postMessage(JSON.stringify({
                            type: TYPE_ALREADY_OPEN,
                            key: publicKeyHex,
                        }));
                    }
                    break;
                case TYPE_ALREADY_OPEN:
                    if (message.key === publicKeyHex && this.stateService.connectionBuildupState !== 'done') {
                        future.resolve('already_open');
                        this.timeoutService.register(() => {
                            this.stateService.updateConnectionBuildupState('already_connected');
                            this.stateService.state = "error";
                        }, 500, true, 'alreadyConnected');
                    }
                    break;
                default:
                    this.log.warn('Unknown broadcast message type:', message.type);
                    break;
            }
        };
        this.log.debug('Checking if the session is already open in another tab or window');
        channel.postMessage(JSON.stringify({
            type: TYPE_PUBLIC_KEY,
            key: publicKeyHex,
        }));
        setTimeout(() => future.resolve('no_answer'), delayMs);
        return future;
    }
    reconnect(keyStore, decrypted) {
        this.webClientService.stop({
            reason: "stop",
            send: false,
            close: 'welcome',
            connectionBuildupState: this.stateService.connectionBuildupState,
        });
        if (decrypted.pushToken !== null && decrypted.pushTokenType !== null) {
            this.webClientService.updatePushToken(decrypted.pushToken, decrypted.pushTokenType);
            this.pushService.init(decrypted.pushToken, decrypted.pushTokenType);
        }
        this.webClientService.init({
            keyStore: keyStore,
            peerTrustedKey: decrypted.peerPublicKey,
            resume: false,
        });
        this.start();
    }
    showBrowserWarning() {
        this.browserWarningShown = true;
        this.$translate.onReady().then(() => {
            const confirm = this.$mdDialog.confirm()
                .title(this.$translate.instant('welcome.BROWSER_NOT_SUPPORTED'))
                .htmlContent(this.$translate.instant('welcome.BROWSER_NOT_SUPPORTED_DETAILS', {
                firefoxLink: '<a href="https://www.mozilla.org/firefox/" target="_blank" rel="noopener noreferrer">Firefox</a>',
                chromeLink: '<a href="https://www.google.com/chrome/browser/desktop/" target="_blank" rel="noopener noreferrer">Chrome</a>',
                operaLink: '<a href="https://www.opera.com/" target="_blank" rel="noopener noreferrer">Opera</a>',
                edgeLink: '<a href="https://www.microsoft.com/edge" target="_blank" rel="noopener noreferrer">Edge</a>',
                safariLink: '<a href="https://www.apple.com/safari/" target="_blank" rel="noopener noreferrer">Safari</a>',
            }))
                .ok(this.$translate.instant('welcome.CONTINUE_ANYWAY'))
                .cancel(this.$translate.instant('welcome.ABORT'));
            this.$mdDialog.show(confirm).then(() => {
            }, () => {
                window.location.replace('https://threema.ch/threema-web');
            });
        });
    }
    showLocalStorageWarning() {
        this.$translate.onReady().then(() => {
            const confirm = this.$mdDialog.alert()
                .title(this.$translate.instant('common.ERROR'))
                .htmlContent(this.$translate.instant('welcome.LOCAL_STORAGE_MISSING_DETAILS'))
                .ok(this.$translate.instant('common.OK'));
            this.$mdDialog.show(confirm);
        });
    }
    showLocalStorageException(e) {
        this.$translate.onReady().then(() => {
            const confirm = this.$mdDialog.alert()
                .title(this.$translate.instant('common.ERROR'))
                .htmlContent(this.$translate.instant('welcome.LOCAL_STORAGE_EXCEPTION_DETAILS', {
                errorMsg: e.name,
            }))
                .ok(this.$translate.instant('common.OK'));
            this.$mdDialog.show(confirm);
        });
    }
    showDecryptionFailed() {
        this.$mdDialog.show({
            controller: DialogController,
            controllerAs: 'ctrl',
            templateUrl: 'partials/dialog.unlockfailed.html',
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            fullscreen: true,
            onRemoving: () => { this.clearPassword(); },
        });
    }
    showAlreadyConnected() {
        this.$translate.onReady().then(() => {
            const confirm = this.$mdDialog.alert()
                .title(this.$translate.instant('welcome.ALREADY_CONNECTED'))
                .htmlContent(this.$translate.instant('welcome.ALREADY_CONNECTED_DETAILS'))
                .ok(this.$translate.instant('common.OK'));
            this.$mdDialog.show(confirm);
        });
    }
    deleteSession(ev) {
        const confirm = this.$mdDialog.confirm()
            .title(this.$translate.instant('common.SESSION_DELETE'))
            .textContent(this.$translate.instant('common.CONFIRM_DELETE_BODY'))
            .targetEvent(ev)
            .ok(this.$translate.instant('common.YES'))
            .cancel(this.$translate.instant('common.CANCEL'));
        this.$mdDialog.show(confirm).then(() => {
            this.mode = 'scan';
            this.clearPassword();
            this.formLocked = false;
            this.scan({
                reason: "delete",
                send: true,
                close: 'welcome',
            });
        }, () => {
        });
    }
    buildQrCode(payload) {
        const len = payload.length;
        let version;
        if (len <= 134) {
            version = 6;
        }
        else if (len <= 154) {
            version = 7;
        }
        else if (len <= 192) {
            version = 8;
        }
        else if (len <= 230) {
            version = 9;
        }
        else if (len <= 271) {
            version = 10;
        }
        else if (len <= 321) {
            version = 11;
        }
        else if (len <= 367) {
            version = 12;
        }
        else if (len <= 425) {
            version = 13;
        }
        else if (len <= 458) {
            version = 14;
        }
        else if (len <= 520) {
            version = 15;
        }
        else if (len <= 586) {
            version = 16;
        }
        else {
            this.log.error('QR Code payload too large: Is your SaltyRTC host string huge?');
            version = 40;
        }
        return {
            version: version,
            errorCorrectionLevel: 'L',
            size: 384,
            margin: 12,
            data: payload,
        };
    }
    clearPassword() {
        this.password = '';
        this.passwordStrength = { score: 0, strength: "bad" };
    }
    start() {
        this.webClientService.start().then(async () => {
            let isAutoPassword = false;
            if (this.inMemorySessionPasswordEnabled) {
                if (this.password.length === 0) {
                    this.log.debug('Generating auto session password');
                    const autoPassword = u8aToHex(nacl.randomBytes(32));
                    this.password = autoPassword;
                    isAutoPassword = true;
                }
                this.inMemorySession.setPassword(this.password);
            }
            const password = this.password;
            this.clearPassword();
            this.formLocked = false;
            this.timeoutService.register(() => this.$state.go('messenger.home'), WelcomeController.REDIRECT_DELAY, true, 'redirectToHome');
            await this.webClientService.setPassword(password, isAutoPassword);
        }, (error) => {
            this.log.error('Error state:', error);
        }, (progress) => { });
    }
    reload() {
        this.$window.location.reload();
    }
    isApnsPushToken() {
        return this.webClientService.pushTokenType === "apns";
    }
    hasAppleNonVoipPushToken() {
        return this.webClientService.hasAppleNonVoipPushToken();
    }
}
angular.module('3ema.welcome', [])
    .config(['$stateProvider', ($stateProvider) => {
        $stateProvider
            .state('welcome', {
            url: '/welcome',
            templateUrl: 'partials/welcome.html',
            controller: 'WelcomeController',
            controllerAs: 'ctrl',
            params: { initParams: null },
        });
    }])
    .controller('WelcomeController', WelcomeController);
